import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import {
  BarChart3,
  Bell,
  Building2,
  Check,
  CheckCircle2,
  ClipboardList,
  Crown,
  FileDown,
  Home,
  LifeBuoy,
  MessageSquare,
  Package,
  Send,
  Settings,
  Sparkles,
  Star,
  Users,
  Wallet,
} from "lucide-react";
import { api, type Tariff, type TariffPlanInfo } from "../api";
import { fmtDate, fmtMoney } from "../lib/format";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";
import { Skeleton } from "../components/ui/skeleton";
import { cn } from "../lib/utils";
import { useTariff } from "../lib/use-tariff";

// Sinov tugashiga qolgan kunlar (0 dan kichik bo'lsa 0). null → sana yo'q.
function trialDaysLeft(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

// Imkoniyat ikonkalari + i18n kalitlari. Label'lar settings namespace orqali
// resolve qilinadi: tariff.features.*
const FEATURE_META: Record<string, { tKey: string; icon: React.ReactNode }> = {
  home: { tKey: "tariff.features.home", icon: <Home className="size-4" /> },
  works: { tKey: "tariff.features.works", icon: <ClipboardList className="size-4" /> },
  clients: { tKey: "tariff.features.clients", icon: <Users className="size-4" /> },
  // Qarz Comfort'dan, ombor esa Pro'dan boshlanadi; entitlementlar server
  // yuborgan plan.features ro'yxatidan keladi.
  tovars: { tKey: "tariff.features.tovars", icon: <Package className="size-4" /> },
  qarzlar: { tKey: "tariff.features.qarzlar", icon: <Wallet className="size-4" /> },
  services: { tKey: "tariff.features.services", icon: <Settings className="size-4" /> },
  reminders: { tKey: "tariff.features.reminders", icon: <Bell className="size-4" /> },
  employees: { tKey: "tariff.features.employees", icon: <Users className="size-4" /> },
  analytics: { tKey: "tariff.features.analytics", icon: <BarChart3 className="size-4" /> },
  branches: { tKey: "tariff.features.branches", icon: <Building2 className="size-4" /> },
  analytics_branch: {
    tKey: "tariff.features.analyticsBranch",
    icon: <BarChart3 className="size-4" />,
  },
  export: { tKey: "tariff.features.export", icon: <FileDown className="size-4" /> },
  tariff: { tKey: "tariff.features.tariff", icon: <Crown className="size-4" /> },
  // Transactional (1-ga-1) va broadcast ALOHIDA imkoniyatlar — broadcast aynan
  // standard'ni comfort'dan ajratib turadigan chiziq, ro'yxatda ko'rinishi shart.
  murojaatlar: { tKey: "tariff.features.murojaatlar", icon: <LifeBuoy className="size-4" /> },
  manual_messaging: {
    tKey: "tariff.features.manualMessaging",
    icon: <MessageSquare className="size-4" />,
  },
  broadcast: { tKey: "tariff.features.broadcast", icon: <Send className="size-4" /> },
};

const TARIFF_ORDER: Tariff[] = ["standard", "comfort", "pro", "max"];

const TARIFF_ICONS: Record<Tariff, React.ReactNode> = {
  trial: <Sparkles />,
  standard: <Star />,
  comfort: <Star />,
  pro: <Sparkles />,
  max: <Crown />,
};

export function TariffPage() {
  const { t } = useTranslation(["settings", "common"]);
  const { status, loading, reload } = useTariff();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  async function activate(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      await api.activate(code.trim());
      toast.success(t("tariff.activated"));
      setCode("");
      await reload();
    } catch (e) {
      const err = e as { message?: string };
      toast.error(err.message || t("tariff.activateFailed"));
    } finally {
      setBusy(false);
    }
  }

  if (loading || !status) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  const current = status.tariff;
  const limits = status.limits;
  const plans: TariffPlanInfo[] =
    status.plans?.length > 0
      ? status.plans
      : TARIFF_ORDER.map((t) => ({
          tariff: t,
          max_users: null,
          max_branches: null,
          max_works: null,
          features: [],
          price: null,
        }));
  // Kumulyativ farq uchun kartalar har doim arzondan qimmatga tartiblanadi.
  const orderedPlans = TARIFF_ORDER.map((id) =>
    plans.find((p) => p.tariff === id),
  ).filter((p): p is TariffPlanInfo => p != null);

  return (
    <div className="space-y-4">
      {/* Current status panel */}
      <Card className="overflow-hidden">
        <div
          className={cn(
            "h-1.5 w-full",
            status.is_expired ? "bg-destructive" : "bg-primary",
          )}
        />
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-start">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center [&_svg]:size-5">
                {TARIFF_ICONS[current] ?? <Star />}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  {t("tariff.currentTariff")}
                </div>
                <div className="text-xl font-bold">
                  {t(`common:tariff.labels.${current}`)}
                </div>
              </div>
              {status.is_expired ? (
                <Badge variant="destructive">{t("tariff.expired")}</Badge>
              ) : status.status === "active" ? (
                <Badge variant="success">{t("tariff.active")}</Badge>
              ) : (
                <Badge variant="warning">{status.status}</Badge>
              )}
            </div>

            {current === "trial" && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {trialDaysLeft(status.trial_end) != null
                      ? t("tariff.freeWithDays", {
                          count: trialDaysLeft(status.trial_end) as number,
                        })
                      : t("tariff.free")}
                  </span>
                  {status.trial_end && (
                    <>
                      {t("tariff.trialEndLabel")}
                      <span className="font-medium text-foreground">
                        {fmtDate(status.trial_end)}
                      </span>
                    </>
                  )}
                </p>
                {/* Sinovda Max darajadagi hamma narsa ochiq — bu hali sotib
                    olingan tarif emas; quyida hech bir karta "Joriy" emas. */}
                <p className="text-sm text-muted-foreground">
                  {t("tariff.trialAccessNote")}
                </p>
              </div>
            )}

            {/* Ish (works) kvotasi endi hech bir tarifda yo'q — o'lchagich ham
                yo'q (∞ ustidagi progress-bar ma'nosiz bo'lardi). Faqat jamoa va
                filial. Qiymatlar token'dagi ustaxonaning O'Z hisoblagichlari. */}
            {limits && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md pt-2">
                <LimitBar
                  label={t("tariff.limits.people")}
                  current={limits.current_users + 1}
                  max={limits.max_users == null ? null : limits.max_users + 1}
                />
                <LimitBar
                  label={t("tariff.limits.branches")}
                  current={limits.current_branches}
                  max={limits.max_branches}
                />
              </div>
            )}
          </div>

          <form
            onSubmit={activate}
            className="flex flex-col gap-2 w-full md:w-72"
          >
            <Label htmlFor="code">{t("tariff.activationCode")}</Label>
            <Input
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder={t("tariff.codePlaceholder")}
              disabled={busy}
              className="font-mono tracking-wider"
            />
            <Button
              type="submit"
              disabled={busy || !code.trim()}
              className="w-full"
            >
              {busy ? t("tariff.activating") : t("tariff.activate")}
            </Button>
            <p className="text-xs text-muted-foreground">
              {t("tariff.codeHint")}
            </p>
          </form>
        </CardContent>
      </Card>

      {/* Plan cards — to'rtta pullik tarif, KUMULYATIV taqdimot: standard to'liq
          ro'yxat, keyingilar faqat QO'SHIMCHALARINI ko'rsatadi ("X'dagi hamma
          narsa, va yana:"). Qo'shimchalar backend yuborgan features to'plamidan
          dasturiy hisoblanadi (plan_limits.py qat'iy ichma-ich — set-farq har
          doim to'g'ri), shuning uchun kartalar koddan uzoqlasha olmaydi. */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {orderedPlans.map((p, i) => (
          <PlanCard
            key={p.tariff}
            plan={p}
            prev={i > 0 ? orderedPlans[i - 1] : null}
            current={current}
            onGoActivate={focusActivationCode}
            t={t}
          />
        ))}
      </div>
    </div>
  );
}

// Aktivatsiya kodi maydoniga olib boradi — kartadagi CTA sahifadagi yagona real
// harakatga (kod kiritish) yo'naltiradi; alohida sotib olish oqimi yo'q.
function focusActivationCode() {
  const el = document.getElementById("code") as HTMLInputElement | null;
  el?.scrollIntoView({ behavior: "smooth", block: "center" });
  el?.focus({ preventScroll: true });
}

function LimitBar({
  label,
  current,
  max,
}: {
  label: string;
  current: number;
  max: number | null;
}) {
  const display = max == null ? `${current} / ∞` : `${current} / ${max}`;
  const pct = max == null ? Math.min(20, current * 5) : (current / max) * 100;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="tabular-nums font-medium">{display}</span>
      </div>
      <Progress
        value={pct}
        indicatorClassName={
          max != null && current >= max ? "bg-warning" : undefined
        }
      />
    </div>
  );
}

function PlanCard({
  plan,
  prev,
  current,
  onGoActivate,
  t,
}: {
  plan: TariffPlanInfo;
  // Zinapoyadagi bitta pastki tarif — kumulyativ farq shu bilan hisoblanadi.
  prev: TariffPlanInfo | null;
  current: Tariff;
  onGoActivate: () => void;
  t: TFunction;
}) {
  const isCurrent = plan.tariff === current;
  const highlighted = plan.tariff === "pro";
  // Kumulyativ ro'yxat: birinchi karta to'liq, qolganlari faqat qo'shimchalar.
  // Backend to'plamlari qat'iy ichma-ich, shuning uchun farq hech qachon
  // "yo'qotish" ko'rsatmaydi. Tartib backend features_list tartibida qoladi.
  const bullets =
    prev == null
      ? plan.features
      : plan.features.filter((f) => !prev.features.includes(f));
  return (
    <Card
      className={cn(
        // Grid stretch + flex-col: hamma karta bir xil balandlikda; CTA pastga
        // mt-auto bilan mixlangan. Lentalar absolute — o'lchamni o'zgartirmaydi.
        "relative flex flex-col",
        highlighted && "border-primary shadow-md shadow-primary/10",
        isCurrent && "ring-2 ring-primary",
        // Tor ekranda (bitta ustun) mijozning joriy tarifi birinchi ko'rinadi.
        isCurrent && "order-first md:order-none",
      )}
    >
      {highlighted && !isCurrent && (
        <div className="absolute top-0 right-4 -translate-y-1/2">
          <Badge>{t("tariff.recommended")}</Badge>
        </div>
      )}
      {isCurrent && (
        <div className="absolute top-0 right-4 -translate-y-1/2">
          <Badge variant="success">
            <CheckCircle2 className="size-3 mr-0.5" />
            {t("tariff.current")}
          </Badge>
        </div>
      )}
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              "size-9 rounded-lg flex items-center justify-center [&_svg]:size-4",
              highlighted
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground",
            )}
          >
            {TARIFF_ICONS[plan.tariff]}
          </div>
          <CardTitle className="text-xl">
            {t(`common:tariff.labels.${plan.tariff}`)}
          </CardTitle>
        </div>
        {/* min-h ikki qatorli tavsifga joy ajratadi — narx hamma kartada bir
            xil balandlikda turadi. */}
        <p className="text-sm text-muted-foreground min-h-10">
          {t(`common:tariff.descriptions.${plan.tariff}`, { defaultValue: "" })}
        </p>
        {plan.price != null && (
          <div className="pt-1">
            <span className="text-2xl font-bold tabular-nums">
              {fmtMoney(plan.price)}
            </span>
            <span className="text-sm text-muted-foreground">{t("tariff.perMonth")}</span>
          </div>
        )}
      </CardHeader>
      <CardContent className="flex-1 flex flex-col gap-4">
        {/* Ish (works) ustuni olib tashlangan — kvota hech bir tarifda yo'q edi
            va ustun har kartada ∞ ko'rsatardi. Jamoa soni EGA BILAN — mijoz
            "xodim hisobi ega tashqarida" degan ichki kelishuvni bilishi shart
            emas (standard: 2 xodim + ega = 3). */}
        <div className="grid grid-cols-2 gap-2 py-3 px-3 bg-muted/50 rounded-lg text-center">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
              {t("tariff.card.people")}
            </div>
            <div className="font-bold tabular-nums">
              {plan.max_users == null ? "∞" : plan.max_users + 1}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
              {t("tariff.card.branches")}
            </div>
            <div className="font-bold tabular-nums">
              {plan.max_branches == null ? "∞" : plan.max_branches}
            </div>
          </div>
        </div>
        {prev != null && (
          // "X'dagi hamma narsa, va yana:" — bu HAVOLA, imkoniyat emas:
          // check-belgisiz, xira rangda, ajratkich bilan vizual farqlanadi.
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">
              {t("tariff.inheritsAll", {
                plan: t(`common:tariff.labels.${prev.tariff}`),
              })}
            </div>
            <Separator />
          </div>
        )}
        <ul className="space-y-2">
          {bullets.map((f) => {
            const info = FEATURE_META[f];
            if (!info) return null;
            return (
              <li key={f} className="flex items-center gap-2 text-sm">
                <Check className="size-4 text-success shrink-0" />
                <span>{t(info.tKey)}</span>
              </li>
            );
          })}
        </ul>
        {/* CTA kartaning PASTIGA mixlangan (mt-auto) — bullet ro'yxati qisqa
            bo'lsa ham hamma kartada bir chiziqda turadi. */}
        <div className="mt-auto pt-2">
          {isCurrent ? (
            <Button variant="secondary" disabled className="w-full">
              {t("tariff.currentPlan")}
            </Button>
          ) : (
            <Button
              variant={highlighted ? "default" : "outline"}
              className="w-full"
              onClick={onGoActivate}
            >
              {t("tariff.goToActivation")}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
